import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { getAssetsWalletLabel } from "../../../utils/walletTypes";
import "./airdropHistory.css";

/**
 * A history row is already paid. Do not use transferredToMainWallet
 * (always false; funds go to staking, not main) or remaining_rounds
 * (future locked rounds) to mark this row pending.
 */
const getRowStatus = (item) => {
  const raw = (item?.status ?? item?.releaseStatus ?? item?.state ?? "")
    .toString()
    .trim()
    .toUpperCase();
  if (raw.includes("FAIL")) return "failed";
  return "released";
};

const getRowToken = (item) => (item?.shortName || "ZILN").toString().toUpperCase();

const getRowWalletLabel = (item) =>
  getAssetsWalletLabel(item?.wallet_type || item?.walletType || "staking");

const STATUS_BADGE = {
  pending: { label: "Pending", className: "ah-badge ah-badge--pending" },
  released: { label: "Released", className: "ah-badge ah-badge--released" },
  locked: { label: "Locked", className: "ah-badge ah-badge--locked" },
  failed: { label: "Failed", className: "ah-badge ah-badge--failed" },
};

const formatDateDisplay = (v) => {
  if (v == null || v === "") return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
};

const truncateMiddle = (str, head = 6, tail = 4) => {
  const s = str == null ? "" : String(str);
  if (s.length <= head + tail + 1) return s || "—";
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const TokenCell = ({ symbol }) => {
  const sym = (symbol || "ZILN").toUpperCase();
  const isUsdt = sym === "USDT";
  const [imgOk, setImgOk] = useState(isUsdt);

  return (
    <div className="ah-token-cell">
      <div className="ah-token-icon-wrap">
        {isUsdt && imgOk ? (
          <img
            src="/images/option-img/usdt_icon.svg"
            alt=""
            onError={() => setImgOk(false)}
          />
        ) : (
          <i className="ri-coins-line ah-token-ri" aria-hidden />
        )}
      </div>
      <span className="ah-token-name">{sym}</span>
    </div>
  );
};

const AirdropHistory = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [vesting, setVesting] = useState({
    totalEarned: 0,
    lockedBalance: 0,
    releasedBalance: 0,
    availableBalance: 0,
    nextUnlockDate: null,
    nextUnlockPercentage: null,
    nextUnlock: null,
    activeVestings: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortLatestFirst, setSortLatestFirst] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let active = true;
    const loadPageData = async () => {
      setLoading(true);
      setError("");
      LoaderHelper.loaderStatus(true);
      try {
        const [historyRes, vestingRes] = await Promise.all([
          AuthService.getReferralReleaseHistory(page, limit),
          AuthService.getReferralVestingStatus(),
        ]);

        if (!active) return;

        if (historyRes?.success) {
          const payload = historyRes?.data || {};
          setRows(Array.isArray(payload?.items) ? payload.items : []);
          setTotal(Number(payload?.total) || 0);
        } else {
          setRows([]);
          setTotal(0);
        }

        if (vestingRes?.success && vestingRes?.data) {
          setVesting((prev) => ({ ...prev, ...vestingRes.data }));
        } else {
          setVesting({
            totalEarned: 0,
            lockedBalance: 0,
            releasedBalance: 0,
            availableBalance: 0,
            nextUnlockDate: null,
            nextUnlockPercentage: null,
            nextUnlock: null,
            activeVestings: [],
          });
        }

        if (!historyRes?.success && !vestingRes?.success) {
          setError("Failed to load referral release data.");
        }
      } catch (e) {
        if (!active) return;
        setRows([]);
        setTotal(0);
        setError(e?.message || "Something went wrong while loading data.");
      } finally {
        if (active) {
          setLoading(false);
        }
        LoaderHelper.loaderStatus(false);
      }
    };

    loadPageData();

    return () => {
      active = false;
      LoaderHelper.loaderStatus(false);
    };
  }, [page, limit]);

  const filteredSortedRows = useMemo(() => {
    let list = Array.isArray(rows) ? [...rows] : [];
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => String(r?.vestingId ?? "").toLowerCase().includes(q));
    }
    if (statusFilter !== "all") {
      list = list.filter((r) => getRowStatus(r) === statusFilter);
    }
    if (sortLatestFirst) {
      list.sort((a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      });
    } else {
      list.sort((a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
      });
    }
    return list;
  }, [rows, searchQuery, statusFilter, sortLatestFirst]);

  const hasApiRows = rows.length > 0;
  const hasDisplayRows = filteredSortedRows.length > 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const filtersActive = Boolean(searchQuery.trim()) || statusFilter !== "all";
  const rowSno = (displayIndex) =>
    filtersActive ? displayIndex + 1 : (page - 1) * limit + displayIndex + 1;

  const listToolbar = (
    <div className="ah-list-toolbar">
      <div className="searchBar custom-tabs">
        <i className="ri-search-2-line" aria-hidden />
        <input
          type="search"
          className="custom_search"
          placeholder="Search vesting ID"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search vesting ID"
        />
      </div>
      <select
        className="form-select form-select-sm ah-toolbar-select"
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        aria-label="Filter by status"
      >
        <option value="all">All status</option>
        <option value="released">Released</option>
        <option value="failed">Failed</option>
      </select>
      <button
        type="button"
        className="ah-sort-toggle"
        aria-pressed={sortLatestFirst}
        onClick={() => setSortLatestFirst((v) => !v)}
      >
        <i className={sortLatestFirst ? "ri-sort-desc" : "ri-sort-asc"} aria-hidden />
        {sortLatestFirst ? "Latest first" : "Oldest first"}
      </button>
      <p className="ah-filter-hint mb-0">
        Filters and sort apply to the current page of results.
      </p>
    </div>
  );

  const pagination = hasApiRows ? (
    <div className="ah-pagination-row hVPalX gap-2">
      <span>
        {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
      </span>
      <div className="sc-eAKtBH gVtWSU">
        <button
          type="button"
          aria-label="First Page"
          className="sc-gjLLEI kuPCgf"
          onClick={() => setPage(1)}
          disabled={page <= 1}
        >
          <i className="ri-skip-back-fill text-white" />
        </button>
        <button
          type="button"
          aria-label="Previous Page"
          className="sc-gjLLEI kuPCgf"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
        >
          <i className="ri-arrow-left-s-line text-white" />
        </button>
        <button
          type="button"
          aria-label="Next Page"
          className="sc-gjLLEI kuPCgf"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
        >
          <i className="ri-arrow-right-s-line text-white" />
        </button>
        <button
          type="button"
          aria-label="Last Page"
          className="sc-gjLLEI kuPCgf"
          onClick={() => setPage(totalPages)}
          disabled={page >= totalPages}
        >
          <i className="ri-skip-forward-fill text-white" />
        </button>
      </div>
    </div>
  ) : null;

  const emptyPremium = (variant) => (
    <div className="ah-empty-premium">
      <div className="ah-empty-premium__icon" aria-hidden>
        <i className="ri-history-line" />
      </div>
      <h3 className="ah-empty-premium__title">
        {variant === "filter" ? "No matching releases" : "No release history yet"}
      </h3>
      <p className="ah-empty-premium__sub">
        {variant === "filter"
          ? "Try adjusting your search or status filter, or go to the next page."
          : "Join the airdrop campaign to earn rewards. Your vesting releases will appear here."}
      </p>
      <Link to="/airdrop" className="btn custom-btn">
        Go to AirDrop
      </Link>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Airdrop History | Zillion Exchange</title>
      </Helmet>

      <div className="dashboard_right">
        <div className="dashboard_listing_section Overview_mid">
          {/* Title + summary cards — outside desktop_view2 so mobile still shows stats */}
          <div className="listing_left_outer full_width transaction_history_t">
            <div className="market_section spotorderhist ah-spot-section">
              <div className="top_heading">
                <h4>Airdrop History</h4>
              </div>

              <div className="row g-2 mb-3 ah-summary-cards">
                <div className="col-6 col-md-6 col-lg-3">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Total Earned</div>
                      <div className="fw-semibold">{toNum(vesting.totalEarned)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-6 col-lg-3">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Locked Balance</div>
                      <div className="fw-semibold">{toNum(vesting.lockedBalance)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-6 col-lg-3">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Released Balance</div>
                      <div className="fw-semibold text-success">{toNum(vesting.releasedBalance)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-6 col-lg-3">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Available Balance</div>
                      <div className="fw-semibold">{toNum(vesting.availableBalance)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {error ? (
                <div className="alert alert-warning mb-3" role="alert">
                  {error}
                </div>
              ) : null}
            </div>
          </div>

          <div className="listing_left_outer full_width transaction_history_t desktop_view2">
            <div className="market_section spotorderhist ah-spot-section">
              {/* Premium list — desktop */}
              <div className="ah-list-premium d-none d-md-block">
                {listToolbar}
                <div className="ah-table-card">
                  <div className="ah-table-scroll">
                    <table className="ah-data-table">
                      <thead>
                        <tr>
                          <th className="ah-col-sno">S.No</th>
                          <th>Round</th>
                          <th>Vesting ID</th>
                          <th>Token</th>
                          <th>Amount</th>
                          <th>Wallet</th>
                          <th className="text-end">Status</th>
                          <th className="text-end">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr className="ah-loading-row">
                            <td colSpan="8">Loading release history…</td>
                          </tr>
                        ) : !hasApiRows ? (
                          <tr>
                            <td colSpan="8" className="p-0 border-0">
                              {emptyPremium("empty")}
                            </td>
                          </tr>
                        ) : !hasDisplayRows ? (
                          <tr>
                            <td colSpan="8" className="p-0 border-0">
                              <div className="ah-empty-filter">{emptyPremium("filter")}</div>
                            </td>
                          </tr>
                        ) : (
                          filteredSortedRows.map((item, index) => {
                            const st = getRowStatus(item);
                            const badge = STATUS_BADGE[st] || STATUS_BADGE.released;
                            const vid = item?.vestingId ?? "—";
                            const token = getRowToken(item);
                            return (
                              <tr key={item?._id ?? `${vid}-${index}`}>
                                <td className="ah-col-sno">{rowSno(index)}</td>
                                <td className="ah-col-round">Round {item?.round ?? "—"}</td>
                                <td>
                                  <span className="ah-vesting-id" title={vid}>
                                    {truncateMiddle(vid, 8, 6)}
                                  </span>
                                </td>
                                <td>
                                  <TokenCell symbol={token} />
                                </td>
                                <td>
                                  <span className="ah-amount">
                                    {toNum(item?.amount)}{" "}
                                    <span className="text-muted fw-semibold">{token}</span>
                                  </span>
                                </td>
                                <td className="ah-wallet-cell">{getRowWalletLabel(item)}</td>
                                <td className="text-end">
                                  <span className={badge.className}>{badge.label}</span>
                                </td>
                                <td className="ah-date-cell text-end">
                                  {formatDateDisplay(item?.createdAt)}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {pagination}
              </div>
            </div>
          </div>

          {/* Premium list — mobile */}
          <div className="order_history_mobile_view d-md-none">
            <div className="ah-list-premium ah-m-premium mt-2">
              <div className="ah-m-toolbar">{listToolbar}</div>
              {loading ? (
                <div className="text-center py-4 small text-muted">Loading release history…</div>
              ) : !hasApiRows ? (
                emptyPremium("empty")
              ) : !hasDisplayRows ? (
                <div className="ah-empty-filter">{emptyPremium("filter")}</div>
              ) : (
                <div className="d-flex flex-column gap-3 w-100">
                  {filteredSortedRows.map((item, index) => {
                    const st = getRowStatus(item);
                    const badge = STATUS_BADGE[st] || STATUS_BADGE.released;
                    const vid = item?.vestingId ?? "—";
                    const token = getRowToken(item);
                    return (
                      <div
                        key={item?._id ?? `${vid}-m-${index}`}
                        className="ah-m-card"
                      >
                        <div className="ah-m-card__top">
                          <div>
                            <div className="ah-m-card__round">
                              Round {item?.round ?? "—"} · #{rowSno(index)}
                            </div>
                            <div className="ah-m-card__amount">
                              {toNum(item?.amount)} {token}
                            </div>
                          </div>
                          <span className={badge.className}>{badge.label}</span>
                        </div>
                        <dl className="ah-m-grid">
                          <dt>Vesting ID</dt>
                          <dd title={vid}>{truncateMiddle(vid, 10, 8)}</dd>
                          <dt>Token</dt>
                          <dd>
                            <TokenCell symbol={token} />
                          </dd>
                          <dt>Wallet</dt>
                          <dd>{getRowWalletLabel(item)}</dd>
                          <dt>Date</dt>
                          <dd>{formatDateDisplay(item?.createdAt)}</dd>
                        </dl>
                      </div>
                    );
                  })}
                </div>
              )}
              {pagination}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AirdropHistory;
